import { useSelector } from "react-redux";
import { Card } from "@app/design-system/components";
import { CustomerNoteIcon } from "@app/design-system/icons";
import { StopIcon } from "@app/features/shared/StopIcon";
import { CustomFieldsNavButton } from "@app/features/shared/CustomFieldsNavButton";
import { StopCompletedBadge } from "@app/features/shared/StopCompletedBadge";
import { RootState } from "@app/store";
import { t } from "@app/utils/translation";
import { cx } from "@app/shared/utils/cx";
import styles from "./DropoffSection.module.css";

export type DropoffSectionProps = {
  address: string;
  /** Epoch ms this dropoff was completed, if it has been — renders the same
   * checkmark + completion time as Order Details' `StopRow` next to the
   * "Dropoff" label when present, hidden otherwise. */
  completedAt?: number;
  /** Rendered as a callout below the address, only when present — see
   * `StopSummary.description` (the stop's customer-facing description,
   * never a proof-related note). */
  description?: string | null;
  /** Proofs + custom fields on this dropoff, summed into the trailing badge
   * — the same `StopSummary.detailsCount` Order Details' `StopRow` uses for
   * its own Dropoff row, so the two stay identical (see
   * `countStopDetailItems`). Gates the badge/arrow nav control exactly like
   * `StopRow` gates it. */
  detailsCount: number;
  onClick: () => void;
  /** Lets `BottomPanel` fuse this flush into its own panel on mobile/tablet,
   * same override pattern as `DriverCard`'s own `className`. */
  className?: string;
};

/**
 * Home's dropoff-tracking section — always shown directly below the
 * driver/last-event slot while `isDropoffTracking` is true (see
 * `BottomPanel`), regardless of whether a driver has been assigned yet.
 *
 * Mirrors Order Details' `StopRow` for the dropoff being tracked: the same
 * `detailsCount`-gated `CustomFieldsNavButton` badge + arrow, navigating to
 * the same Stop Details page, and the same `StopCompletedBadge` (checkmark +
 * completion time) once `completedAt` is set. Only that trailing control is
 * clickable — the icon/label/address row itself is inert, same rule as
 * StopRow/CustomerInfoRow/DriverCard. The customer-description callout is
 * its own piece below, outside that row.
 */
export const DropoffSection = ({
  address,
  completedAt,
  description,
  detailsCount,
  onClick,
  className,
}: DropoffSectionProps) => {
  const language = useSelector((state: RootState) => state.setting.language);
  const hasDetails = detailsCount > 0;

  return (
    <Card className={cx(styles.card, className)}>
      <div className={styles.row}>
        <StopIcon kind="dropoff" className={styles.icon} />
        <div className={styles.body}>
          <div className={styles.header}>
            <div className={styles.label}>{t("Dropoff", language)}</div>
            {completedAt !== undefined ? <StopCompletedBadge completedAt={completedAt} /> : null}
          </div>
          <div className={styles.value}>{address}</div>
        </div>
        {hasDetails ? (
          <CustomFieldsNavButton
            count={detailsCount}
            onClick={onClick}
            ariaLabel={`${t("ViewStopDetails", language)} (${detailsCount})`}
          />
        ) : null}
      </div>
      {description ? (
        <div className={styles.note}>
          <CustomerNoteIcon size={16} className={styles.noteIcon} />
          <span>{description}</span>
        </div>
      ) : null}
    </Card>
  );
};
